import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  buildCloseRunPayload,
  cashSummary,
  CommandValidationError,
  endRunSummary,
  expectedBack,
  precheckScanBack,
  type AddScanResult,
} from '@ecobox/firebase';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  ScanInput,
  ScannerView,
  Textarea,
  scanFeedback,
  useToast,
  type ScanVerdict,
} from '@ecobox/ui';
import { ScanRows } from '../../components/ScanRows';
import { useDriverData } from '../../hooks/useDriver';
import { useRunBranch } from '../../hooks/useRunBranch';
import { useWakeLock } from '../../hooks/useWakeLock';
import { loadBarcodeDetector } from '../../scanner/detector';
import { useLedger, useServices, useSession } from '../../services';
import { useAllSessions, useLedgerById, useSessionEngine } from '../../sessions';
import { BackLink, Problems, Tile, fmt } from './shared';

const VERDICT: Record<AddScanResult['tone'], ScanVerdict> = { ok: 'ok', warn: 'warn', bad: 'bad' };

/** End the run (05 §5.2): scan back every undelivered box, then hand the cash to the cashier. */
export function EndRunPage() {
  const { runId = '' } = useParams();
  const data = useDriverData();
  const { uid } = useSession();
  const entries = useLedger();
  const ledger = useLedgerById();
  const engine = useSessionEngine();
  const sessions = useAllSessions();
  const { writer } = useServices();
  const toast = useToast();
  const run = data.runs.find((r) => r.id === runId);
  const stops = useMemo(() => data.stopsOf(runId) ?? [], [data, runId]);
  const [camera, setCamera] = useState(false);
  const [last, setLast] = useState<AddScanResult | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useRunBranch(run?.branchId);
  useWakeLock(run?.status === 'active');

  const session = sessions.find(
    (s) =>
      s.kind === 'run_return' &&
      s.targetId === runId &&
      s.uid === uid &&
      s.finishedAt === undefined,
  );
  const ended = sessions.find(
    (s) =>
      s.kind === 'run_return' &&
      s.targetId === runId &&
      s.uid === uid &&
      s.finishedAt !== undefined,
  );

  const expected = useMemo(
    () => expectedBack({ runId, stops, runBoxes: data.boxesOf(runId), entries }),
    [runId, stops, data, entries],
  );

  if (!run) {
    return (
      <EmptyState
        title={data.loading ? 'Loading your run…' : 'This run is not on your phone'}
        action={
          <Link to="/today">
            <Button size="touch">Back to Today</Button>
          </Link>
        }
      />
    );
  }

  const scannedList = (session?.scans ?? []).filter(
    (s) => s.status === 'buffered' || s.status === 'queued',
  );
  const scanned = new Set(scannedList.map((s) => s.barcode));
  const summary = endRunSummary({ expected, scanned, stops });
  const cash = cashSummary({ run, stops, custody: data.custody, entries });
  const closeCommand = ended?.closeCommandId ? ledger.get(ended.closeCommandId) : undefined;
  const stopOf = new Map(
    stops.flatMap((s) => s.expectedBarcodes.map((b) => [b, s.label] as const)),
  );

  const handleScan = (raw: string): ScanVerdict => {
    if (!uid) return 'bad';
    const barcode = raw.trim().toUpperCase();
    const s =
      session ??
      engine.start({
        kind: 'run_return',
        targetId: run.id,
        targetLabel: `Scan-back · ${run.date}`,
        uid,
        branchId: run.branchId,
      });
    const result = engine.addScan(s.id, barcode, precheckScanBack({ barcode, expected }));
    setLast(result);
    return VERDICT[result.tone];
  };

  const finish = () => {
    setBusy(true);
    try {
      if (session && session.scans.some((s) => s.status === 'buffered')) {
        engine.flush(session.id);
        const fresh = engine.get(session.id);
        const batch = fresh?.batches[fresh.batches.length - 1];
        if (!batch) throw new Error(fresh?.lastError ?? 'The scan-back could not be saved.');
        engine.markClosed(session.id, batch.commandId);
      } else {
        // Nothing to scan back: the run still closes, with an empty list.
        const sent = writer.send(
          'closeRun',
          buildCloseRunPayload({ runId: run.id, barcodesBack: [], note: session?.extra?.['note'] }),
        );
        if (session) engine.markClosed(session.id, sent.id);
        else {
          const started = engine.start({
            kind: 'run_return',
            targetId: run.id,
            targetLabel: `Scan-back · ${run.date}`,
            uid: uid ?? '',
            branchId: run.branchId,
          });
          engine.markClosed(started.id, sent.id);
        }
      }
      toast.show({
        title: 'Run ended',
        message: 'Saved. It goes to the server when you have signal.',
        tone: 'success',
      });
    } catch (err) {
      toast.show({
        title: 'Not ended',
        message:
          err instanceof CommandValidationError && err.issues.length > 0
            ? err.issues.join('; ')
            : err instanceof Error
              ? err.message
              : String(err),
        tone: 'danger',
      });
    } finally {
      setBusy(false);
    }
  };

  // ---- already ended: the cash to hand over, and what the server said ----
  if (ended || run.status === 'closed') {
    const rejected = closeCommand?.state === 'rejected';
    return (
      <div className="ops-stack">
        <BackLink to="/today">Today</BackLink>
        <h1 className="ops-title">Run ended</h1>
        {rejected ? (
          <div role="alert" className="ops-last" data-tone="bad">
            <div>The server refused to end this run: {closeCommand?.lastError?.message}</div>
            {ended ? (
              <Button size="lg" variant="secondary" onClick={() => engine.reopen(ended.id)}>
                Scan back again
              </Button>
            ) : null}
          </div>
        ) : (
          <p
            role="status"
            className={closeCommand?.state === 'applied' ? 'ops-ok' : undefined}
            style={{ margin: 0 }}
          >
            {closeCommand?.state === 'applied' ||
            closeCommand?.state === 'applied_with_exception' ||
            (!closeCommand && run.status === 'closed' && !run.waiting)
              ? 'The run is closed.'
              : closeCommand?.state === 'not_delivered'
                ? 'Saved on this phone, but it has not reached the server. Resend it from Me.'
                : 'Saved on this phone. It reaches the server when you have signal.'}
          </p>
        )}
        <CashCard cash={cash} />
        <Link to="/today">
          <Button size="touch" variant="primary" block>
            Back to Today
          </Button>
        </Link>
      </div>
    );
  }

  const warnings: string[] = [];
  if (summary.openStops > 0) {
    warnings.push(
      `${summary.openStops} stop${summary.openStops === 1 ? ' is' : 's are'} still not done or failed.`,
    );
  }
  if (summary.missing.length > 0) {
    warnings.push(
      `${summary.missing.length} box${summary.missing.length === 1 ? '' : 'es'} not scanned back. Each is raised as missing for the office.`,
    );
  }

  return (
    <div className="ops-stack">
      <BackLink to={`/run/${run.id}`}>The run</BackLink>
      <h1 className="ops-title">End the run</h1>

      <Problems problems={data.problemsOf(run.id)} />

      <Card title="Boxes to scan back" data-tour="scan-back">
        <div className="ops-stack">
          <div className="ops-tiles">
            <Tile n={summary.expected} label="to come back" />
            <Tile n={summary.scannedBack} label="scanned back" tone="ok" />
            <Tile
              n={summary.missing.length}
              label="still out"
              tone={summary.missing.length > 0 ? 'wait' : 'ok'}
            />
          </div>
          {expected.fromPlan && expected.barcodes.length > 0 ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              This phone does not know what was loaded, so the plan is used as the list.
            </p>
          ) : null}
          {summary.expected === 0 ? (
            <p style={{ margin: 0 }}>Every box was delivered. Nothing to scan back.</p>
          ) : (
            <>
              <div data-tour="scan-field">
                <ScanInput
                  onScan={(code) => scanFeedback(handleScan(code))}
                  status={
                    last
                      ? last.tone === 'bad'
                        ? 'error'
                        : last.tone === 'ok'
                          ? 'ok'
                          : 'idle'
                      : 'idle'
                  }
                />
              </div>
              {last ? (
                <div className="ops-last" data-tone={last.tone} role="status">
                  <span className="ops-code">{last.barcode}</span> {last.message}
                </div>
              ) : null}
              <Button size="touch" variant="cta" block onClick={() => setCamera(true)}>
                Scan with the camera
              </Button>
            </>
          )}
          {summary.missing.length > 0 ? (
            <ul className="ops-list" aria-label="Still out">
              {summary.missing.slice(0, 20).map((b) => (
                <li key={b} className="ops-row" data-tone="waiting">
                  <div className="ops-row__top">
                    <span className="ops-code">{b}</span>
                    <Badge tone="warning">Not back</Badge>
                  </div>
                  {stopOf.get(b) ? <div className="ops-muted">{stopOf.get(b)}</div> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {summary.missing.length > 20 ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              …and {summary.missing.length - 20} more.
            </p>
          ) : null}
        </div>
      </Card>

      {session && session.scans.length > 0 ? (
        <section className="ops-stack" aria-label="Scanned back">
          <h2 className="ops-subtitle">Scanned back</h2>
          <ScanRows
            session={session}
            ledger={ledger}
            label="Scans"
            onRemove={(b) => engine.removeScan(session.id, b)}
          />
        </section>
      ) : null}

      <CashCard cash={cash} />

      <div data-tour="end-note">
        <Field label="Note for the office (optional)">
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={300}
              defaultValue={session?.extra?.['note'] ?? ''}
              onChange={(e) => {
                const note = e.target.value;
                const s =
                  session ??
                  (uid
                    ? engine.start({
                        kind: 'run_return',
                        targetId: run.id,
                        targetLabel: `Scan-back · ${run.date}`,
                        uid,
                        branchId: run.branchId,
                      })
                    : undefined);
                if (s) engine.setExtra(s.id, 'note', note);
              }}
            />
          )}
        </Field>
      </div>

      {warnings.length > 0 ? (
        <div role="alert" className="ops-last" data-tone="warn">
          {warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
      ) : null}

      <div className="ops-actionbar" data-tour="end-actions">
        {warnings.length > 0 && !confirm ? (
          <Button size="touch" variant="primary" block onClick={() => setConfirm(true)}>
            End the run anyway
          </Button>
        ) : (
          <Button size="touch" variant="cta" block loading={busy} onClick={finish}>
            {warnings.length > 0 ? 'Yes, end the run' : 'End the run'}
          </Button>
        )}
      </div>

      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title={`End the run · ${summary.scannedBack} of ${summary.expected} back`}
          count={summary.scannedBack}
          countLabel={`of ${summary.expected}`}
          lastResult={
            last ? { code: last.barcode, tone: VERDICT[last.tone], message: last.message } : null
          }
          onRead={handleScan}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}

/** What to put in the cashier's hand (04 §4.5 `closeRun`: "driver must hand over"). */
function CashCard({ cash }: { cash: ReturnType<typeof cashSummary> }) {
  return (
    <Card title="Cash to hand to the cashier" data-tour="cash-handover">
      <div className="ops-stack">
        <div className="ops-money" aria-label="Cash to hand over">
          {fmt(cash.toHandOverMinor, cash.currency)}
        </div>
        <p className="ops-muted" style={{ margin: 0 }}>
          Taken on this run: {fmt(cash.collectedMinor, cash.currency)}
          {cash.waitingMinor > 0
            ? ` (${fmt(cash.waitingMinor, cash.currency)} still waiting to sync)`
            : ''}
          . Held in your name: {fmt(cash.heldMinor, cash.currency)}.
        </p>
        {cash.lines.length > 0 ? (
          <ul className="ops-list" aria-label="Cash by stop">
            {cash.lines.map((l) => (
              <li key={l.stopId} className="ops-row">
                <div className="ops-row__top">
                  <span>{l.label}</span>
                  <strong>{fmt(l.amountMinor, cash.currency)}</strong>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ops-muted" style={{ margin: 0 }}>
            No cash was taken at any stop.
          </p>
        )}
      </div>
    </Card>
  );
}
